import {
  Check,
  CircleOff,
  Clock,
  Eye,
  Funnel,
  LayoutGrid,
  List,
  Newspaper,
  Sparkles,
  X,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { IconHint } from '../../../chrome/hint';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../ui/popover';
import { TooltipProvider } from '../../../ui/tooltip';
import { KIND_ICONS } from './cells';
import { kindFilterLabel, pub } from './messages';
import type { SortKey } from './readings';
import { SegmentedControl } from './SegmentedControl';
import { WINDOW_KEYS, type WindowKey } from './window';

export type ViewKey = 'feed' | 'list';

export interface ToolbarState {
  window: WindowKey;
  sort: SortKey;
  view: ViewKey;
  tags: string[];
}

export type RecorteKey = 'all' | 'related' | 'unrelated';

export type KindFilterKey = 'all' | 'POST' | 'REEL' | 'STORY';

export const KIND_FILTER_KEYS: KindFilterKey[] = ['all', 'POST', 'REEL', 'STORY'];

function kindFilterIcon(key: KindFilterKey): ReactNode {
  const Icon = key === 'all' ? LayoutGrid : KIND_ICONS[key]!;
  return <Icon aria-hidden className="size-4" />;
}

/** The campaign's tags as a multi-select; each chosen tag shows as a
 *  removable chip beside the trigger. */
function PostFilter(props: {
  tags: string[];
  tagOptions: string[];
  onTags: (tags: string[]) => void;
}): ReactNode {
  const { tags, onTags } = props;
  const active = tags.length > 0;

  function toggle(tag: string): void {
    onTags(tags.includes(tag) ? tags.filter((each) => each !== tag) : [...tags, tag]);
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {tags.map((tag) => (
        <Badge key={tag} variant="secondary" className="gap-1 pr-1">
          {tag}
          <IconHint label={pub('filter.remove', { tag })}>
            <button
              type="button"
              onClick={() => toggle(tag)}
              className="hover:text-foreground focus-visible:ring-ring rounded-full focus-visible:ring-2 focus-visible:outline-none"
            >
              <X aria-hidden className="size-3" />
            </button>
          </IconHint>
        </Badge>
      ))}
      {tags.length > 0 && (
        <Button variant="ghost" size="sm" onClick={() => onTags([])}>
          {pub('filter.clear')}
        </Button>
      )}
      <Popover>
        <IconHint label={pub('filter.open')}>
          <PopoverTrigger asChild>
            <Button variant={active ? 'secondary' : 'ghost'} size="icon">
              <Funnel aria-hidden />
            </Button>
          </PopoverTrigger>
        </IconHint>
        <PopoverContent align="end" className="flex w-80 flex-col gap-3 p-3">
          <Command className="border">
            <CommandInput placeholder={pub('filter.search')} />
            <CommandList>
              <CommandEmpty>{pub('filter.none')}</CommandEmpty>
              <CommandGroup heading={pub('filter.label')}>
                {props.tagOptions.map((tag) => (
                  <CommandItem key={tag} value={tag} onSelect={() => toggle(tag)}>
                    <Check
                      aria-hidden
                      className={tags.includes(tag) ? 'size-4' : 'size-4 opacity-0'}
                    />
                    {tag}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}

/** The window, kind filter, Recorte, ordering, view mode and tag filter. Shared by both
 *  presentations so switching between them keeps every choice. */
export function PublicationsToolbar(props: {
  state: ToolbarState;
  onWindow: (window: WindowKey) => void;
  onSort: (sort: SortKey) => void;
  onView: (view: ViewKey) => void;
  onTags: (tags: string[]) => void;
  tagOptions: string[];
  kindFilter: KindFilterKey;
  onKindFilter: (kind: KindFilterKey) => void;
  recorte: RecorteKey;
  onRecorte: (recorte: RecorteKey) => void;
}): ReactNode {
  const { state } = props;
  return (
    <TooltipProvider>
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl
          label={pub('window.label')}
          value={state.window}
          onChange={props.onWindow}
          options={WINDOW_KEYS.map((key) => ({ value: key, label: key }))}
        />
        <div className="min-w-2 flex-1" />
        <SegmentedControl
          label={pub('kindFilter.label')}
          value={props.kindFilter}
          onChange={props.onKindFilter}
          options={KIND_FILTER_KEYS.map((key) => ({
            value: key,
            label: kindFilterLabel(key),
            icon: kindFilterIcon(key),
          }))}
        />
        <SegmentedControl
          label={pub('recorte.label')}
          value={props.recorte}
          onChange={props.onRecorte}
          options={[
            {
              value: 'all',
              label: pub('recorte.all'),
              icon: <LayoutGrid aria-hidden className="size-4" />,
            },
            {
              value: 'related',
              label: pub('recorte.related'),
              icon: <Sparkles aria-hidden className="size-4" />,
            },
            {
              value: 'unrelated',
              label: pub('recorte.unrelated'),
              hint: pub('recorte.unrelated.hint'),
              icon: <CircleOff aria-hidden className="size-4" />,
            },
          ]}
        />
        <SegmentedControl
          label={pub('sort.label')}
          value={state.sort}
          onChange={props.onSort}
          options={[
            {
              value: 'recent',
              label: pub('sort.recent'),
              icon: <Clock aria-hidden className="size-4" />,
            },
            {
              value: 'reach',
              label: pub('sort.reach'),
              icon: <Eye aria-hidden className="size-4" />,
            },
          ]}
        />
        <SegmentedControl
          label={pub('view.label')}
          value={state.view}
          onChange={props.onView}
          options={[
            {
              value: 'feed',
              label: pub('view.feed'),
              icon: <Newspaper aria-hidden className="size-4" />,
            },
            {
              value: 'list',
              label: pub('view.list'),
              icon: <List aria-hidden className="size-4" />,
            },
          ]}
        />
        <PostFilter tags={state.tags} tagOptions={props.tagOptions} onTags={props.onTags} />
      </div>
    </TooltipProvider>
  );
}
